import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { SearchX } from 'lucide-react';
import { useIntl } from 'react-intl';
import CardCategories from './cardcategories';
import { normalizePortalText, PortalFilter, PortalPagination } from './portal-controls';
import { portalColors, portalFocus } from '../portal-styles';

const PAGE_SIZE = 10;

export default function PortalCategoryServices({ categories }) {
  const intl = useIntl();
  const [query, setQuery] = React.useState('');
  const [page, setPage] = React.useState(1);
  const showSearch = categories.length > 5;
  const filtered = showSearch
    ? categories.filter(category => normalizePortalText(`${category.name || ''} ${category.parentname || ''}`).includes(normalizePortalText(query.trim())))
    : categories;
  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const currentPage = Math.min(page, Math.max(1, pageCount));

  return <Box component="section" aria-labelledby="portal-category-services-title" sx={{ width: '100%', alignSelf: 'stretch' }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: 2.5, mb: 2 }}>
      <Box>
        <Typography id="portal-category-services-title" component="h2" sx={{ fontSize: 21, fontWeight: 500, color: portalColors.ink }}>
          {intl.formatMessage({ id: 'portal.category.services' })}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.75, color: portalColors.muted }} aria-live="polite" aria-atomic="true">
          {intl.formatMessage({ id: 'portal.category.services.count' }, { count: filtered.length })}
          {' · '}{intl.formatMessage({ id: 'portal.category.services.subtitle' })}
        </Typography>
      </Box>
      {showSearch && <Box sx={{ width: { xs: '100%', sm: 300 }, maxWidth: '100%', flexShrink: 0 }}>
        <PortalFilter id="portal-category-service-search" label={intl.formatMessage({ id: 'portal.category.search.label' })}
          value={query} onChange={value => { setQuery(value); setPage(1); }} />
      </Box>}
    </Box>
    {filtered.length ? <Box>
      {filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map(category => <Box key={category.id} sx={{ py: 0.75, borderBottom: `1px solid ${portalColors.line}` }}>
        <CardCategories element={category} variant="service" />
      </Box>)}
      <PortalPagination page={currentPage} count={pageCount} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
    </Box> : <Box role="status" sx={{ py: 5, px: 3, bgcolor: portalColors.surface, borderRadius: '20px', color: portalColors.muted }}>
      <SearchX size={30} strokeWidth={1.5} aria-hidden="true" />
      <Typography sx={{ fontWeight: 500, mt: 2, color: portalColors.ink }}>
        {intl.formatMessage({ id: query ? 'portal.category.search.empty.title' : 'portal.category.children.empty' })}
      </Typography>
      {query && <>
        <Typography variant="body2" sx={{ mt: 0.75 }}>{intl.formatMessage({ id: 'portal.category.search.empty.description' })}</Typography>
        <Button color="inherit" onClick={() => { setQuery(''); setPage(1); }} sx={{ ...portalFocus, mt: 2, textTransform: 'none', borderRadius: '999px' }}>{intl.formatMessage({ id: 'portal.category.search.clear' })}</Button>
      </>}
    </Box>}
  </Box>;
}
